import { z } from 'zod';
import { MemoryTypeSchema } from './agent-memory.js';
/**
 * The memory audit trail (DEV-17253, memory-architecture §4.8): the Zod
 * contract over `agent_memory_event` rows, which are written TRANSACTIONALLY
 * inside the shared write path (`@cortex/database/memory-write` and the
 * maintenance lanes) — the one place every runtime's mutations flow through,
 * including the persistent-agent host's in-process writes that the HTTP-only
 * `AuditLogEntry` interceptor can never see. Once approval is mostly
 * automatic (DEV-17256), this trail is the only remaining control — and the
 * digest below is what makes it a trail somebody reads instead of an alibi.
 */
/**
 * What happened to the memory. One event row per row-mutation:
 *  - `created`    — a new row (payload carries status/source, and, for an
 *                   auto-approved create, the covering policy ids);
 *  - `edited`     — a copy-on-write version cut (recorded on the SURVIVING
 *                   N+1 row; payload links the archived predecessor). A judge
 *                   promotion (type → PRINCIPLE) is an `edited` attributed to
 *                   the judge, per the ticket;
 *  - `approved` / `rejected` — a human (or policy) review decision;
 *  - `archived`   — a deliberate human/agent archive (soft-terminal);
 *  - `expired`    — the working-tier TTL sweep retired the row;
 *  - `merged`     — the row was folded into a covering row (`mergedInto`):
 *                   the maintenance merge pass, an authoritative reconcile
 *                   NOOP/UPDATE fold, or the working-tier compaction fold
 *                   into the daily summary;
 *  - `pruned`     — the decay sweep archived a disused row, or the
 *                   working-tier cap evicted overflow (THE reason code that
 *                   distinguishes a sweep from a human archive, §4.8);
 *  - `superseded` — an authoritative reconcile correction archived the row
 *                   pointing at its replacement;
 *  - `judged`     — the principle judge recorded a verdict (actor = the
 *                   judge model; payload carries the verdict summary and
 *                   token counts when available);
 *  - `reconciled` — the reconcile pass recorded a verdict (actor = the
 *                   reconcile model; payload carries op/applied/target).
 * Deliberately NOT evented: `duplicateSaveCount` bumps (the counter itself
 * is the aggregate record — an event per bump would double-write the same
 * signal) and per-row decay ticks (hourly × hundreds of rows of noise; the
 * terminal `pruned` event carries the final importance instead).
 */
export const MemoryEventKindSchema = z.enum([
    'created',
    'edited',
    'approved',
    'rejected',
    'archived',
    'expired',
    'merged',
    'pruned',
    'superseded',
    'judged',
    'reconciled',
]);
/**
 * The actor grammar for `actorSubject` (also used for `approvedBySubject`):
 * `user:<id>` (a human principal) | `service:<subject>` (a service token —
 * e.g. the MCP server acting for an agent) | `agent:<persistentAgentId>` |
 * `policy:<policyId>` | `judge:<provider/modelId>` |
 * `reconcile:<provider/modelId>` | `sweep:maintenance`. Free-form string at
 * the DB level; these builders/prefixes are the ONE place the grammar lives,
 * because the write side (memory-write, the API transitions) and the read
 * side (the digest's `startsWith` filters) must never drift on it.
 */
export const memoryActorSubject = {
    user: (subject) => `user:${subject}`,
    service: (subject) => `service:${subject}`,
    agent: (persistentAgentId) => `agent:${persistentAgentId}`,
    policy: (policyId) => `policy:${policyId}`,
    judge: (model) => `judge:${model}`,
    reconcile: (model) => `reconcile:${model}`,
};
/** Prefixes for `startsWith` reads (the digest's promotion filter, guards). */
export const memoryJudgeActorPrefix = 'judge:';
export const memoryReconcileActorPrefix = 'reconcile:';
export const memoryAgentActorPrefix = 'agent:';
export const memorySweepActorSubject = 'sweep:maintenance';
export const AgentMemoryEventSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    memoryId: z.string(),
    kind: MemoryEventKindSchema,
    actorSubject: z.string().nullable(),
    reason: z.string().nullable(),
    payload: z.record(z.string(), z.unknown()).nullable(),
    createdAt: z.date(),
});
// ─── The review digest (§4.7: "the trail must be read, not merely written") ──
/** One recently auto-approved memory, as the digest presents it. */
export const MemoryDigestAutoApprovedSchema = z.object({
    memoryId: z.string(),
    content: z.string(),
    type: MemoryTypeSchema,
    /** Agent definitions the row is bound to (who it teaches). */
    agentIds: z.array(z.string()),
    approvedAt: z.string().datetime(),
    /** The policy that blessed it — survives the policy's deletion. */
    approvedByPolicyId: z.string().nullable(),
    createdBySubject: z.string().nullable(),
});
/** One judge promotion (type → PRINCIPLE), as the digest presents it. */
export const MemoryDigestPromotionSchema = z.object({
    memoryId: z.string(),
    content: z.string(),
    /** `judge:<provider/modelId>` that promoted it. */
    actorSubject: z.string().nullable(),
    promotedAt: z.string().datetime(),
});
/**
 * "What did agents teach themselves this week" — the one skimmable view
 * §4.7 requires before auto-approval widens (DEV-17256). Windowed; cheap
 * (row columns + two event-log scans, both index-served).
 */
export const MemoryDigestSchema = z.object({
    /** Window start (ISO); everything below is since this instant. */
    since: z.string().datetime(),
    autoApproved: z.array(MemoryDigestAutoApprovedSchema),
    judgePromotions: z.array(MemoryDigestPromotionSchema),
    /** Event counts by kind inside the window — the trail's pulse. */
    eventCounts: z.record(z.string(), z.number().int().nonnegative()),
});
//# sourceMappingURL=memory-events.js.map